import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { type ButtonDropdownItem, IconType } from '@factorialco/f0-react'
import { Delete, EyeVisible, Pencil } from '@factorialco/f0-react/icons/app'
import { PayrollPolicyPeriodTypesPeriodTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { MissingCategoriesModal } from 'modules/expenses/components/expenses/AddToPayrollModal/_MissingCategoriesModal'
import ReimburseModal from 'modules/expenses/components/expenses/AddToPayrollModal/_ReimburseModal'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import DeleteGroupModal from 'modules/expenses/components/groups/DeleteModal'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import {
  Group,
  useAddToPayroll,
  useDelete,
  useRemoveFromPayroll,
  useSendForApproval,
} from 'modules/expenses/hooks/useGroup'
import { ModalAction } from 'modules/expenses/lib/expensables/modalAction'
import {
  canDeleteGroup,
  canEditGroup,
  canRemoveGroupFromPayroll,
  canSendGroupForApproval,
} from 'modules/expenses/lib/groups/availableActions'
import { isDraftGroup, isRejectedGroup } from 'modules/expenses/lib/groups/group'

function showFlashMessage<Data>(
  handled: Result<Data>,
  {
    success,
    error,
  }: {
    success: string
    error: string
  }
) {
  if (handled.success) {
    app.setFlash({
      type: 'correct',
      message: success,
    })
  } else {
    app.setFlash({
      type: 'wrong',
      message: error,
    })
  }
}

interface ModalsProps {
  action: ModalAction | null
  anyAutomaticExpense: boolean
  anyMissingPayrollCategory: boolean
  currentPayrollPeriodOpen: boolean
  onReject?: (reason?: string) => void
  onGlobalApprove?: () => void
  onAddToPayroll?: (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => void
  onDelete: () => void
  onClose: () => void
}

interface UsePageActionsProps {
  group?: Group
  currentPayrollPeriodOpen: boolean
  reimburseThroughPayrollEnabled: boolean
}

interface UsePageActionsReturn {
  actions: ExpenseGroupActions
  Modals: FC
}

export interface ExpenseGroupActions {
  primary: PrimaryExpenseGroupAction | PrimaryExpenseGroupDropdownAction | undefined
  secondary: SecondaryExpenseGroupAction[]
}

export interface SecondaryExpenseGroupAction extends PrimaryExpenseGroupAction {
  variant?: 'outline' | 'critical'
}

export interface PrimaryExpenseGroupAction {
  label: string
  icon?: IconType
  onClick: () => void
}

export interface PrimaryExpenseGroupDropdownAction {
  items: ButtonDropdownItem<string>[]
  value?: string
  onClick: (value: string, item: ButtonDropdownItem<string>) => void
}

const Modals = ({
  action,
  anyAutomaticExpense,
  anyMissingPayrollCategory,
  currentPayrollPeriodOpen,
  onReject,
  onGlobalApprove,
  onAddToPayroll,
  onDelete,
  onClose,
}: ModalsProps) => {
  const handleOnReview = () => {
    onReject?.()
  }

  const handleOnGlobalApprove = () => {
    onGlobalApprove?.()
  }

  const handleOnAddToPayroll = (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => {
    onAddToPayroll?.(period)
  }

  return (
    <>
      {action === ModalAction.Reject && (
        <ReviewModal
          type="reject"
          title={i18n.t('expenses_v2.group_detail.modals.reject.title')}
          onSubmit={handleOnReview}
          onClose={onClose}
        />
      )}
      {action === ModalAction.GlobalApprove && (
        <ReviewModal
          type="approve"
          title={i18n.t('expenses_v2.list.actions.modal.title_global_approve')}
          description={i18n.t('expenses_v2.list.actions.modal.description_global_approve')}
          onSubmit={handleOnGlobalApprove}
          onClose={onClose}
        />
      )}
      {action === ModalAction.CreatePayrollSupplement &&
        (anyMissingPayrollCategory ? (
          <MissingCategoriesModal onClose={onClose} />
        ) : (
          <ReimburseModal
            onSubmit={handleOnAddToPayroll}
            onClose={onClose}
            currentPeriodOpen={currentPayrollPeriodOpen}
          />
        ))}
      {action === ModalAction.Delete && (
        <DeleteGroupModal
          anyAutomaticExpense={anyAutomaticExpense}
          onSubmit={onDelete}
          onClose={onClose}
        />
      )}
    </>
  )
}

export const usePageActions = ({
  group,
  currentPayrollPeriodOpen,
  reimburseThroughPayrollEnabled,
}: UsePageActionsProps): UsePageActionsReturn => {
  const { employee, company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()

  const [action, setAction] = useState<ModalAction | null>(null)

  const { sendForApproval, loading: sendingForApproval } = useSendForApproval()
  const { addToPayroll, loading: addingToPayroll } = useAddToPayroll()
  const { removeFromPayroll, loading: removingFromPayroll } = useRemoveFromPayroll()
  const { delete: destroy, loading: deleting } = useDelete()

  if (!group) return { actions: { primary: undefined, secondary: [] }, Modals: () => null }

  const primary: (PrimaryExpenseGroupAction | PrimaryExpenseGroupDropdownAction)[] = []
  const secondary: SecondaryExpenseGroupAction[] = []

  const onSendForApproval = async () => {
    const handled = handleResult(await sendForApproval(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.send_for_approval.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.send_for_approval.error'),
    })
  }

  const onAddToPayroll = async (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => {
    const handled = handleResult(await addToPayroll(group.id, period, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.add_to_payroll.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.add_to_payroll.error'),
    })
  }

  const onRemoveFromPayroll = async () => {
    const handled = handleResult(await removeFromPayroll(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.remove_from_payroll.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.remove_from_payroll.error'),
    })
  }

  const onDelete = async () => {
    const handled = handleResult(await destroy(group.id, company.id), () => null)
    showFlashMessage(handled, {
      success: i18n.t('expenses_v2.group_detail.flash_messages.delete.success'),
      error: i18n.t('expenses_v2.group_detail.flash_messages.delete.error'),
    })
    if (handled.success) navigate(navigation.list.fullPath())
  }

  if (canSendGroupForApproval(group, employee)) {
    primary.push({
      label: i18n.t('expenses_v2.buttons.send_for_approval'),
      onClick: () => !sendingForApproval && onSendForApproval(),
    })
  }

  if (canRemoveGroupFromPayroll(group, reimburseThroughPayrollEnabled)) {
    primary.push({
      label: i18n.t('expenses_v2.buttons.remove_from_payroll'),
      onClick: () => !removingFromPayroll && onRemoveFromPayroll(),
    })
  }

  if (group.addableToPayroll && reimburseThroughPayrollEnabled) {
    primary.push({
      label: i18n.t('expenses_v2.buttons.reimburse_through_payroll'),
      onClick: () => !addingToPayroll && setAction(ModalAction.CreatePayrollSupplement),
    })
  }

  if (canDeleteGroup(group, employee?.id) && isRejectedGroup(group)) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.delete_group'),
      icon: Delete,
      variant: 'critical',
      onClick: () => !deleting && setAction(ModalAction.Delete),
    })
  }

  if (canDeleteGroup(group, employee?.id) && isDraftGroup(group)) {
    secondary.push({
      label: i18n.t('expenses_v2.buttons.delete_group'),
      icon: Delete,
      variant: 'critical',
      onClick: () => !deleting && setAction(ModalAction.Delete),
    })
  }

  secondary.push({
    label: canEditGroup(group, employee?.id)
      ? i18n.t('expenses_v2.buttons.edit_group')
      : i18n.t('expenses_v2.buttons.group_details'),
    onClick: () => navigate(navigation.groups.id.edit.fullPath({ groupId: group.id })),
    icon: canEditGroup(group, employee?.id) ? Pencil : EyeVisible,
  })

  return {
    actions: { primary: primary[0], secondary },
    Modals: () => (
      <Modals
        action={action}
        currentPayrollPeriodOpen={currentPayrollPeriodOpen}
        anyAutomaticExpense={group.anyAutomaticExpense}
        anyMissingPayrollCategory={group.anyMissingPayrollCategory}
        onAddToPayroll={onAddToPayroll}
        onDelete={onDelete}
        onClose={() => setAction(null)}
      />
    ),
  }
}
